import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FileText, Eye, CheckCircle2, Copy, Send, X, AlertCircle, 
  Mail, FileSignature, Loader2, ChevronDown, Users, Plus, Trash2, UserCheck
} from 'lucide-react';
import { apiService } from '../../services/api';

export default function UploadForm({
  generatedSigners = [],
  uploadError, 
  loading,
  onUpload, 
  onClear, 
  onCopyLink, 
  onSendEmail
}) {
  const [mode, setMode] = useState('file');
  const [file, setFile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // 👇 Cada firmante ahora tiene: fullName, email, Y un selectedClientId propio
  const [signers, setSigners] = useState([{ fullName: '', email: '', selectedClientId: '' }]);

  const hasLoadedClients = useRef(false);
  const hasLoadedTemplates = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (mode === 'template' && !hasLoadedTemplates.current) {
      hasLoadedTemplates.current = true;
      loadTemplates();
    }
  }, [mode]);

  useEffect(() => {
    if (!hasLoadedClients.current) {
      hasLoadedClients.current = true;
      loadClients();
    }
  }, []);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const data = await apiService.request('/templates');
      if (data.success) {
        setTemplates(data.templates);
        if (data.templates.length > 0) {
          setSelectedTemplateId(data.templates[0].id);
        }
      }
    } catch (err) {
      console.error("Error al cargar plantillas:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const data = await apiService.request('/clients');
      if (data.success) {
        setClients(data.clients);
      }
    } catch (err) {
      console.error("Error al cargar clientes:", err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert("El archivo supera el límite de 10 MB.");
        return;
      }
      if (selectedFile.type !== 'application/pdf') {
        alert("Solo se permiten archivos PDF.");
        return;
      }
      setFile(selectedFile);
    }
  };

  // 👇 NUEVO: Maneja la selección de cliente para un firmante ESPECÍFICO
  const handleSignerClientSelect = (signerIndex, clientId) => {
    const updatedSigners = [...signers];
    updatedSigners[signerIndex].selectedClientId = clientId;
    
    const client = clients.find(c => c.id === clientId);
    if (client) {
      updatedSigners[signerIndex].fullName = client.fullName;
      updatedSigners[signerIndex].email = client.email || '';
    } else {
      // Si deselecciona, solo limpia el ID, mantiene lo que haya escrito
      updatedSigners[signerIndex].selectedClientId = '';
    }
    
    setSigners(updatedSigners);
  };

  // 👇 NUEVO: Cuando el usuario escribe manualmente, limpiamos el selectedClientId
  // para que el select vuelva a la opción "-- Seleccionar --"
  const handleSignerChange = (index, field, value) => {
    const updatedSigners = [...signers];
    updatedSigners[index][field] = value;
    
    // Si el usuario modifica manualmente el nombre, desvinculamos del cliente seleccionado
    if (field === 'fullName') {
      const selectedClient = clients.find(c => c.id === updatedSigners[index].selectedClientId);
      if (selectedClient && selectedClient.fullName !== value) {
        updatedSigners[index].selectedClientId = '';
      }
    }
    
    setSigners(updatedSigners);
  };

  const addSigner = () => {
    // 👇 Cada nuevo firmante inicia con su propio selectedClientId vacío
    setSigners([...signers, { fullName: '', email: '', selectedClientId: '' }]);
  };

  const removeSigner = (index) => {
    if (signers.length > 1) {
      const updatedSigners = signers.filter((_, i) => i !== index);
      setSigners(updatedSigners);
    }
  };

  const handleSubmit = () => {
    if (!signers[0].fullName.trim()) {
      alert("El nombre del primer firmante es obligatorio.");
      return;
    }

    const invalidSigners = signers.some(s => !s.fullName.trim());
    if (invalidSigners) {
      alert("Todos los firmantes deben tener un nombre completo.");
      return;
    }

    const formData = new FormData();
    formData.append("expiresInHours", 72);
    // 👇 Enviamos solo fullName y email (no selectedClientId, que es solo UI)
    formData.append("signers", JSON.stringify(
      signers.map(s => ({ fullName: s.fullName, email: s.email }))
    ));

    if (mode === 'file') {
      if (!file) {
        alert("Por favor selecciona un archivo PDF.");
        return;
      }
      formData.append("document", file);
    } else {
      if (!selectedTemplateId) {
        alert("Por favor selecciona una plantilla.");
        return;
      }
      formData.append("templateId", selectedTemplateId);
    }

    onUpload(formData);
  };

  const handleClear = () => {
    setFile(null);
    setSelectedTemplateId(templates.length > 0 ? templates[0].id : '');
    setSigners([{ fullName: '', email: '', selectedClientId: '' }]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* ================= COLUMNA IZQUIERDA: FORMULARIO ================= */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Nuevo Documento</h2>
          <p className="text-slate-500 mt-1.5 text-sm">
            Sube un archivo o usa una plantilla y agrega a todos los firmantes necesarios.
          </p>
        </div>

        {uploadError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 font-medium">{uploadError}</p>
          </div>
        )}

        <div className="space-y-5">
          {/* Selector de Modo */}
          <div className="flex p-1 bg-slate-100 rounded-xl">
            <button
              type="button"
              onClick={() => setMode('file')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-all ${
                mode === 'file' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Upload className="w-4 h-4" />
              Subir Archivo
            </button>
            <button
              type="button"
              onClick={() => setMode('template')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-all ${
                mode === 'template' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              Usar Plantilla
            </button>
          </div>

          {/* Contenido según el modo */}
          {mode === 'file' ? (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Archivo PDF <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`
                  group border-2 border-dashed rounded-xl p-6 text-center cursor-pointer 
                  transition-all duration-300 flex flex-col items-center justify-center min-h-[140px]
                  ${file ? 'border-blue-500 bg-blue-50/50' : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50/50'}
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                {file ? (
                  <>
                    <FileText className="w-8 h-8 text-blue-600 mb-2" />
                    <p className="text-sm font-semibold text-slate-900 truncate max-w-[90%]">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB • Clic para cambiar</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 group-hover:text-blue-600 mb-2 transition-colors" />
                    <p className="text-sm font-medium text-slate-600">Haz clic o arrastra tu PDF aquí</p>
                    <p className="text-xs text-slate-400 mt-1">Máximo 10 MB</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Seleccionar Plantilla <span className="text-red-500">*</span>
              </label>
              {loadingTemplates ? (
                <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando plantillas...
                </div>
              ) : templates.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  No tienes plantillas activas. 
                  <a href="#" className="font-semibold underline ml-1" onClick={(e) => { e.preventDefault(); }}>
                    Crea una aquí
                  </a>.
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              )}
            </div>
          )}

          {/* ================= SECCIÓN DE FIRMANTES MÚLTIPLES ================= */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-semibold text-slate-700">
                Firmantes <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addSigner}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Agregar otro firmante
              </button>
            </div>
            
            {/* 👇 Mensaje si no hay clientes guardados */}
            {loadingClients && (
              <div className="flex items-center gap-2 p-3 mb-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando clientes guardados...
              </div>
            )}
            
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div key={index} className="relative p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                  {/* Header del firmante */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Firmante {index + 1}
                    </span>
                    {signers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSigner(index)}
                        className="text-slate-400 hover:text-red-600 transition-colors p-1"
                        title="Eliminar firmante"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* 👇 NUEVO: Selector de cliente guardado para ESTE firmante */}
                  {clients.length > 0 && (
                    <div className="relative">
                      <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                      <select
                        value={signer.selectedClientId}
                        onChange={(e) => handleSignerClientSelect(index, e.target.value)}
                        className="w-full appearance-none bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-9 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer hover:bg-blue-100/50"
                      >
                        <option value="">
                          -- Seleccionar cliente guardado (o escribir manualmente) --
                        </option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.fullName} {client.dni ? `• DNI: ${client.dni}` : ''} {client.email ? `• ${client.email}` : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
                    </div>
                  )}
                  
                  {/* Input Nombre completo */}
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={signer.fullName}
                      onChange={(e) => handleSignerChange(index, 'fullName', e.target.value)}
                      placeholder="Nombre completo"
                      className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  {/* Input Email */}
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={signer.email}
                      onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
                      placeholder="Correo electrónico (opcional)"
                      className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  {/* 👇 Indicador visual si fue autocompletado desde cliente guardado */}
                  {signer.selectedClientId && (
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 w-fit">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Datos cargados desde clientes guardados</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading || !signers[0].fullName || (mode === 'file' && !file) || (mode === 'template' && !selectedTemplateId)}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-600/10"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <FileSignature className="w-4 h-4" />
                  Generar Links de Firma
                </>
              )}
            </button>

            <button
              onClick={handleClear}
              type="button"
              disabled={loading}
              className="sm:w-auto w-full px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:text-red-600 hover:border-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* ================= COLUMNA DERECHA: VISTA PREVIA / ÉXITO ================= */}
      <div className="space-y-6">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 uppercase tracking-wide">
          <Eye className="w-4 h-4 text-slate-500" />
          Resumen del Documento
        </h3>

        {generatedSigners.length > 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-emerald-900">¡Documento Creado!</h3>
                <p className="text-sm text-emerald-700 mt-0.5">
                  Se han generado los enlaces para {generatedSigners.length} firmante(s).
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {generatedSigners.map((signer, index) => (
                <div key={index} className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-slate-900">{signer.fullName}</span>
                    </div>
                    {signer.email && (
                      <span className="text-xs text-slate-500 flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-md">
                        <Mail className="w-3 h-3" /> {signer.email}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={signer.signatureLink}
                      readOnly
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    />
                    <button
                      onClick={() => onCopyLink(signer.signatureLink)}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-xs flex items-center gap-1.5 shadow-sm flex-shrink-0"
                      title="Copiar enlace"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Copiar</span>
                    </button>
                  </div>
                </div>
              ))}

              {generatedSigners.some(s => s.email) && (
                <button
                  onClick={onSendEmail}
                  className="w-full py-2.5 bg-white border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors font-medium text-sm flex items-center justify-center gap-2 mt-2"
                >
                  <Send className="w-4 h-4" />
                  Enviar todos los enlaces por correo
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 min-h-[300px] flex flex-col">
            {(mode === 'file' && file) || (mode === 'template' && selectedTemplateId) ? (
              <div className="space-y-5 flex-1">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                    mode === 'file' ? 'bg-blue-50 border-blue-100' : 'bg-purple-50 border-purple-100'
                  }`}>
                    {mode === 'file' ? (
                      <FileText className="w-6 h-6 text-blue-600" />
                    ) : (
                      <FileSignature className="w-6 h-6 text-purple-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {mode === 'file' ? file.name : templates.find(t => t.id === selectedTemplateId)?.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${mode === 'file' ? 'bg-blue-400' : 'bg-purple-400'}`}></span>
                      {mode === 'file' ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Plantilla base'}
                    </p>
                  </div>
                </div>

                {signers.some(s => s.fullName) && (
                  <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm space-y-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Firmantes ({signers.filter(s => s.fullName).length})
                    </p>
                    {signers.filter(s => s.fullName).map((signer, idx) => (
                      <div key={idx} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                        <Users className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{signer.fullName}</p>
                          {signer.email && (
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Mail className="w-3 h-3" /> {signer.email}
                            </p>
                          )}
                          {signer.selectedClientId && (
                            <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-0.5">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Desde clientes guardados
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <FileSignature className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Esperando datos</p>
                <p className="text-xs text-slate-500 max-w-[200px]">
                  Selecciona un archivo o plantilla y agrega a los firmantes.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}